import React, { useEffect, useMemo, useState, useRef, Children, Fragment, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronDown, ChevronRight, FolderOpen, LayoutGrid, List, Search, X } from 'lucide-react';
import { InteriorMenuItem, InteriorOption } from '../../types/interiorConfig';
import { useSelections } from './SelectionsContext';
import { useTheme } from '../styles/ThemeContext';
// ── Types ──
type ViewMode = 'tabs' | 'folders';
interface SubVariantTab {
  item: InteriorMenuItem;
}
interface SubVariantGalleryProps {
  parentItem: InteriorMenuItem;
  onClose: () => void;
  onSelectOption: (optionId: string) => void;
  category?: string;
}
// ── Price / Tier Utilities (mirrored from OptionsGallery) ──
const getOptionPrice = (optionId: string): number => {
  const baseId = optionId.replace(/^lots-/, '').replace(/-[2-5]$/, '');
  const prices: Record<string, number> = {
    standard: 0,
    'level-1': 1500,
    'level-2': 2500,
    'level-3': 3500,
    'level-4': 4500,
    'black-electric': 2000,
    'black-gas': 2200,
    'fridge-standard': 0,
    'fridge-french-door': 1800,
    'fridge-side-by-side': 1200,
    'fridge-counter-depth': 2400,
    'fridge-smart': 3500,
    'fridge-panel-ready': 2800,
    'dw-standard': 0,
    'dw-quiet': 450,
    'dw-third-rack': 650,
    'dw-panel-ready': 850,
    'dw-smart': 1200,
    'mw-standard': 0,
    'mw-over-range': 350,
    'mw-drawer': 1200,
    'mw-built-in': 800,
    'ct-laminate': 0,
    'ct-solid-surface': 450,
    'ct-ceramic-tile': 900,
    'ct-butcher-block': 1400,
    'ct-concrete': 1900,
    'ct-recycled-glass': 2400,
    'ct-granite': 2900,
    'ct-soapstone': 3400,
    'ct-quartz': 3900,
    'ct-quartzite': 4800,
    'ct-marble': 5800,
    'ct-porcelain-slab': 6800,
    'ct-dekton': 7800,
    'ct-calacatta': 8500,
    // Flooring
    'fl-hardwood-oak': 0,
    'fl-hardwood-walnut': 1800,
    'fl-engineered-hickory': 1200,
    'fl-lvp-standard': 600,
    'fl-porcelain-tile': 2200,
    'fl-ceramic-tile': 900,
    'fl-natural-stone': 4500,
    'fl-herringbone': 3800,
    // Backsplash
    'bs-subway-white': 0,
    'bs-subway-gray': 200,
    'bs-herringbone': 800,
    'bs-hexagon': 650,
    'bs-marble-slab': 3500,
    'bs-glass-mosaic': 1200,
    'bs-zellige': 1800,
    'bs-penny-round': 950,
    'bs-natural-stone': 2800,
    // Sub-variant items (posts, caps, rails, balusters, materials, handles, etc.)
    'post-square': 0,
    'post-turned': 250,
    'post-craftsman': 400,
    'cap-flat': 0,
    'cap-ball': 150,
    'cap-acorn': 200,
    'hr-round': 0,
    'hr-flat': 180,
    'hr-craftsman': 350,
    'bal-plain': 0,
    'bal-twisted': 500,
    'bal-basket': 650,
    'bal-wood': 300,
    'rail-mat-oak': 0,
    'rail-mat-maple': 200,
    'rail-mat-iron': 800,
    'rail-mat-painted': 150,
    'hm-bar': 0,
    'hm-knob': 100,
    'hm-cup': 180,
    'hm-edge': 250,
    'hm-ring': 300,
    'hmat-nickel': 0,
    'hmat-black': 120,
    'hmat-chrome': 200,
    'hmat-brass': 350,
    'hmat-bronze': 400,
    'cp-shaker': 0,
    'cp-flat': 800,
    'cp-raised': 1200,
    'cp-beadboard': 1500
  };
  return prices[optionId] ?? prices[baseId] ?? Math.floor(Math.random() * 800) + 100;
};
const getTier = (price: number): string => {
  if (price === 0) return 'Standard';
  if (price <= 500) return 'Level 1';
  if (price <= 1000) return 'Level 2';
  if (price <= 1500) return 'Level 3';
  if (price <= 2000) return 'Level 4';
  if (price <= 2500) return 'Level 5';
  if (price <= 3000) return 'Level 6';
  if (price <= 3500) return 'Level 7';
  if (price <= 4000) return 'Level 8';
  if (price <= 5000) return 'Level 9';
  if (price <= 6000) return 'Level 10';
  if (price <= 7000) return 'Level 11';
  if (price <= 8000) return 'Level 12';
  return 'Level 13';
};
const getTierColor = (tier: string, isDark: boolean) => {
  switch (tier) {
    case 'Standard':
      return isDark ? 'text-emerald-400 bg-emerald-500/20 border-emerald-500/30' : 'text-emerald-700 bg-emerald-100 border-emerald-200';
    case 'Level 1':
      return isDark ? 'text-blue-400 bg-blue-500/20 border-blue-500/30' : 'text-blue-700 bg-blue-100 border-blue-200';
    case 'Level 2':
      return isDark ? 'text-amber-400 bg-amber-500/20 border-amber-500/30' : 'text-amber-700 bg-amber-100 border-amber-200';
    case 'Level 3':
      return isDark ? 'text-purple-400 bg-purple-500/20 border-purple-500/30' : 'text-purple-700 bg-purple-100 border-purple-200';
    case 'Level 4':
      return isDark ? 'text-rose-400 bg-rose-500/20 border-rose-500/30' : 'text-rose-700 bg-rose-100 border-rose-200';
    case 'Level 5':
      return isDark ? 'text-cyan-400 bg-cyan-500/20 border-cyan-500/30' : 'text-cyan-700 bg-cyan-100 border-cyan-200';
    case 'Level 6':
      return isDark ? 'text-orange-400 bg-orange-500/20 border-orange-500/30' : 'text-orange-700 bg-orange-100 border-orange-200';
    case 'Level 7':
      return isDark ? 'text-lime-400 bg-lime-500/20 border-lime-500/30' : 'text-lime-700 bg-lime-100 border-lime-200';
    case 'Level 8':
      return isDark ? 'text-indigo-400 bg-indigo-500/20 border-indigo-500/30' : 'text-indigo-700 bg-indigo-100 border-indigo-200';
    case 'Level 9':
      return isDark ? 'text-pink-400 bg-pink-500/20 border-pink-500/30' : 'text-pink-700 bg-pink-100 border-pink-200';
    case 'Level 10':
      return isDark ? 'text-teal-400 bg-teal-500/20 border-teal-500/30' : 'text-teal-700 bg-teal-100 border-teal-200';
    case 'Level 11':
      return isDark ? 'text-fuchsia-400 bg-fuchsia-500/20 border-fuchsia-500/30' : 'text-fuchsia-700 bg-fuchsia-100 border-fuchsia-200';
    case 'Level 12':
      return isDark ? 'text-sky-400 bg-sky-500/20 border-sky-500/30' : 'text-sky-700 bg-sky-100 border-sky-200';
    case 'Level 13':
      return isDark ? 'text-red-400 bg-red-500/20 border-red-500/30' : 'text-red-700 bg-red-100 border-red-200';
    default:
      return isDark ? 'text-gray-400 bg-gray-500/20 border-gray-500/30' : 'text-gray-700 bg-gray-100 border-gray-200';
  }
};
const TIER_ORDER: Record<string, number> = {
  Standard: 0,
  'Level 1': 1,
  'Level 2': 2,
  'Level 3': 3,
  'Level 4': 4,
  'Level 5': 5,
  'Level 6': 6,
  'Level 7': 7,
  'Level 8': 8,
  'Level 9': 9,
  'Level 10': 10,
  'Level 11': 11,
  'Level 12': 12,
  'Level 13': 13
};
// ── Helpers ──
function hasDescendantOptions(item: InteriorMenuItem): boolean {
  if (item.options && item.options.length > 0) return true;
  if (item.children) return item.children.some(c => hasDescendantOptions(c));
  return false;
}
function buildTabs(parent: InteriorMenuItem): SubVariantTab[] {
  const tabs: SubVariantTab[] = [];
  if (parent.options && parent.options.length > 0) {
    tabs.push({
      item: parent
    });
  }
  if (!parent.children) return tabs;
  for (const child of parent.children) {
    if (!hasDescendantOptions(child)) continue;
    // Only add children that have direct options (no deeper nesting)
    if (child.options && child.options.length > 0) {
      tabs.push({
        item: child
      });
    }
  }
  return tabs;
}
function countDescendantOptions(item: InteriorMenuItem): number {
  let count = item.options?.length ?? 0;
  if (item.children) {
    for (const child of item.children) count += countDescendantOptions(child);
  }
  return count;
}
function countConfigurableLeaves(item: InteriorMenuItem): number {
  let count = 0;
  if (item.options && item.options.length > 0) count++;
  if (item.children) {
    for (const child of item.children) count += countConfigurableLeaves(child);
  }
  return count;
}
// Enrich options with price/tier
type EnrichedOption = InteriorOption & {
  price: number;
  tier: string;
};
function enrichOptions(options: InteriorOption[]): EnrichedOption[] {
  return options.map(opt => {
    const price = getOptionPrice(opt.id);
    return {
      ...opt,
      price,
      tier: getTier(price)
    };
  });
}
function filterAndSortOptions(options: EnrichedOption[], searchQuery: string, selectedTiers: Set<string>): EnrichedOption[] {
  return options.filter(opt => {
    const tierMatch = selectedTiers.size === 0 || selectedTiers.has(opt.tier);
    const searchMatch = !searchQuery.trim() || opt.name.toLowerCase().includes(searchQuery.toLowerCase());
    return tierMatch && searchMatch;
  }).sort((a, b) => {
    const tierDiff = (TIER_ORDER[a.tier] ?? 99) - (TIER_ORDER[b.tier] ?? 99);
    if (tierDiff !== 0) return tierDiff;
    return a.price - b.price;
  });
}
// ── TierMultiSelect ──
function TierMultiSelect({
  selectedTiers,
  onToggleTier,
  optionTierCounts,
  totalCount,
  isDark
}: {
  selectedTiers: Set<string>;
  onToggleTier: (tier: string) => void;
  optionTierCounts: Record<string, number>;
  totalCount: number;
  isDark: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);
  const allSelected = selectedTiers.size === 0;
  const selectedCount = selectedTiers.size;
  let buttonLabel = 'All Levels';
  if (selectedCount === 1) buttonLabel = Array.from(selectedTiers)[0];else if (selectedCount > 1) buttonLabel = `${selectedCount} Levels`;
  const tierOptions = ['Standard', 'Level 1', 'Level 2', 'Level 3', 'Level 4', 'Level 5', 'Level 6', 'Level 7', 'Level 8', 'Level 9', 'Level 10', 'Level 11', 'Level 12', 'Level 13'];
  return <div ref={dropdownRef} className="relative" data-id="element-2276">
      <button onClick={e => {
      e.stopPropagation();
      setIsOpen(!isOpen);
    }} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all whitespace-nowrap border
          ${isDark ? 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10 hover:text-white' : 'bg-gray-100 border-gray-200 text-gray-600 hover:bg-gray-200 hover:text-gray-900'}
          ${isOpen ? isDark ? 'bg-white/10 border-white/20 text-white' : 'bg-gray-200 border-gray-300 text-gray-900' : ''}`} data-id="element-2277">
        <span data-id="element-2278">{buttonLabel}</span>
        <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-2279" />
      </button>
      <AnimatePresence data-id="element-2280">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -4,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -4,
        scale: 0.97
      }} transition={{
        duration: 0.15,
        ease: [0.22, 1, 0.36, 1]
      }} className={`absolute top-full left-0 mt-1.5 z-50 min-w-[180px] max-h-[320px] rounded-xl border shadow-2xl backdrop-blur-xl overflow-y-auto
              ${isDark ? 'bg-slate-900/95 border-white/10' : 'bg-white/95 border-gray-200'}`} onClick={e => e.stopPropagation()} data-id="element-2281">
            <button onClick={() => onToggleTier('All')} className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors
                ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'}
                ${allSelected ? isDark ? 'bg-cyan-500/10' : 'bg-teal-500/5' : ''}`} data-id="element-2282">
              <div className={`w-4 h-4 rounded flex items-center justify-center border transition-all flex-shrink-0
                ${allSelected ? isDark ? 'bg-cyan-500 border-cyan-400' : 'bg-teal-600 border-teal-500' : isDark ? 'border-white/20 bg-white/5' : 'border-gray-300 bg-white'}`} data-id="element-2283">
                {allSelected && <Check className="w-3 h-3 text-white" data-id="element-2284" />}
              </div>
              <span className={`text-xs font-medium ${isDark ? 'text-white/80' : 'text-gray-700'}`} data-id="element-2285">
                All Levels
              </span>
              <span className={`text-[10px] ml-auto ${isDark ? 'text-white/30' : 'text-gray-400'}`} data-id="element-2286">
                {totalCount}
              </span>
            </button>
            <div className={`mx-3 border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`} data-id="element-2287" />
            {tierOptions.map(tier => {
          const count = optionTierCounts[tier] || 0;
          if (count === 0) return null;
          const isChecked = selectedTiers.has(tier);
          const tierColor = getTierColor(tier, isDark);
          return <button key={tier} onClick={() => onToggleTier(tier)} className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors
                    ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'}
                    ${isChecked ? isDark ? 'bg-cyan-500/10' : 'bg-teal-500/5' : ''}`} data-id="element-2288">
                  <div className={`w-4 h-4 rounded flex items-center justify-center border transition-all flex-shrink-0
                    ${isChecked ? isDark ? 'bg-cyan-500 border-cyan-400' : 'bg-teal-600 border-teal-500' : isDark ? 'border-white/20 bg-white/5' : 'border-gray-300 bg-white'}`} data-id="element-2289">
                    {isChecked && <Check className="w-3 h-3 text-white" data-id="element-2290" />}
                  </div>
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${tierColor}`} data-id="element-2291">
                    {tier}
                  </span>
                  <span className={`text-[10px] ml-auto ${isDark ? 'text-white/30' : 'text-gray-400'}`} data-id="element-2292">
                    {count}
                  </span>
                </button>;
        })}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
// ── Scrolling name overlay ──
function MarqueeName({
  name,
  isDark
}: {
  name: string;
  isDark: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [overflow, setOverflow] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  useEffect(() => {
    if (containerRef.current && textRef.current) {
      const diff = textRef.current.scrollWidth - containerRef.current.clientWidth;
      setOverflow(diff > 2 ? diff + 8 : 0);
    }
  }, [name]);
  return <div ref={containerRef} className="overflow-hidden px-1" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} data-id="element-2293">
      <span ref={textRef} className={`block whitespace-nowrap text-[9px] font-bold text-center uppercase tracking-wider ${isDark ? 'text-white/90' : 'text-gray-800'}`} style={{
      transform: isHovered && overflow > 0 ? `translateX(-${overflow}px)` : 'translateX(0)',
      transition: isHovered && overflow > 0 ? `transform ${Math.max(1, overflow / 40)}s ease-in-out` : 'transform 0.3s ease-out'
    }} title={name} data-id="element-2294">
        {name}
      </span>
    </div>;
}
// ── Option Card ──
function OptionCard({
  option,
  isSelected,
  onSelect,
  isDark
}: {
  option: EnrichedOption;
  isSelected: boolean;
  onSelect: () => void;
  isDark: boolean;
}) {
  const isIncluded = option.price === 0;
  return <div className="group relative flex flex-col items-center gap-2 flex-shrink-0" data-id="element-2295">
      <button onClick={onSelect} className="relative flex flex-col items-center gap-1 transition-all text-left w-full" data-id="element-2296">
        <div className={`relative w-24 h-24 rounded-xl overflow-hidden border-2 transition-all duration-300
          ${isSelected ? isDark ? 'border-cyan-500 shadow-[0_0_15px_rgba(6,182,212,0.4)]' : 'border-teal-500 shadow-[0_0_15px_rgba(20,184,166,0.4)]' : isDark ? 'border-white/10 group-hover:border-white/30' : 'border-gray-200 group-hover:border-gray-300'}`} data-id="element-2297">
          {option.thumbnail ? <img src={option.thumbnail} alt={option.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-2298" /> : <div className={`w-full h-full flex items-center justify-center ${isDark ? 'bg-slate-800' : 'bg-gray-100'}`} data-id="element-2299">
              <span className={`text-[10px] ${isDark ? 'text-white/20' : 'text-gray-300'}`} data-id="element-2300">
                No Image
              </span>
            </div>}
          <div className={`absolute bottom-0 inset-x-0 py-1 overflow-hidden backdrop-blur-md ${isDark ? 'bg-black/60' : 'bg-white/80'}`} data-id="element-2301">
            <MarqueeName name={option.name} isDark={isDark} data-id="element-2302" />
          </div>
          {isSelected && <div className={`absolute inset-0 flex items-center justify-center ${isDark ? 'bg-cyan-500/20' : 'bg-teal-500/20'}`} data-id="element-2303">
              <div className={`rounded-full p-1 ${isDark ? 'bg-cyan-500' : 'bg-teal-500'}`} data-id="element-2304">
                <Check className="w-3 h-3 text-white" data-id="element-2305" />
              </div>
            </div>}
        </div>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full
          ${isIncluded ? isDark ? 'text-white/30' : 'text-gray-400' : isDark ? 'bg-white/10 text-white/60' : 'bg-gray-100 text-gray-600'}`} data-id="element-2306">
          {isIncluded ? 'Included' : `+$${option.price.toLocaleString()}`}
        </span>
      </button>
    </div>;
}
// ── Folder Card ──
function FolderCard({
  item,
  onClick,
  isDark,
  hasSelection
}: {
  item: InteriorMenuItem;
  onClick: () => void;
  isDark: boolean;
  hasSelection: boolean;
}) {
  const optionCount = countDescendantOptions(item);
  const leafCount = countConfigurableLeaves(item);
  const hasChildren = !!(item.children && item.children.length > 0);
  const hasDirectOptions = !!(item.options && item.options.length > 0);
  const previewThumbnail = item.options?.[0]?.thumbnail || item.children?.find(c => c.options?.[0]?.thumbnail)?.options?.[0]?.thumbnail;
  return <button onClick={onClick} className="group relative flex flex-col items-center gap-2 flex-shrink-0 transition-all" data-id="element-2307">
      <div className={`relative w-28 h-28 rounded-xl overflow-hidden border-2 transition-all duration-300 flex flex-col items-center justify-center gap-1.5
        ${hasSelection ? isDark ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-emerald-400/40 bg-emerald-50' : isDark ? 'border-white/10 bg-white/[0.03] group-hover:border-white/25 group-hover:bg-white/[0.06]' : 'border-gray-200 bg-gray-50 group-hover:border-gray-300 group-hover:bg-gray-100'}`} data-id="element-2308">
        {previewThumbnail ? <>
            <img src={previewThumbnail} alt={item.name} className="absolute inset-0 w-full h-full object-cover opacity-20 group-hover:opacity-30 transition-opacity" data-id="element-2309" />
            <div className="relative z-10 flex flex-col items-center gap-1" data-id="element-2310">
              <FolderOpen className={`w-6 h-6 ${isDark ? 'text-white/50 group-hover:text-white/70' : 'text-gray-400 group-hover:text-gray-600'} transition-colors`} data-id="element-2311" />
              {hasChildren && <ChevronRight className={`w-3.5 h-3.5 ${isDark ? 'text-white/30' : 'text-gray-300'} group-hover:translate-x-0.5 transition-transform`} data-id="element-2312" />}
            </div>
          </> : <>
            <FolderOpen className={`w-7 h-7 ${isDark ? 'text-white/40 group-hover:text-white/60' : 'text-gray-400 group-hover:text-gray-600'} transition-colors`} data-id="element-2313" />
            {hasChildren && <ChevronRight className={`w-3.5 h-3.5 ${isDark ? 'text-white/30' : 'text-gray-300'} group-hover:translate-x-0.5 transition-transform`} data-id="element-2314" />}
          </>}
        <div className={`absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded-md text-[9px] font-bold ${isDark ? 'bg-white/10 text-white/50' : 'bg-gray-200 text-gray-500'}`} data-id="element-2315">
          {hasDirectOptions && !hasChildren ? `${optionCount}` : `${leafCount} ${leafCount === 1 ? 'group' : 'groups'}`}
        </div>
        {hasSelection && <div className={`absolute top-1.5 left-1.5 w-2 h-2 rounded-full ${isDark ? 'bg-emerald-400' : 'bg-emerald-500'}`} data-id="element-2316" />}
      </div>
      <span className={`text-[10px] font-bold uppercase tracking-wider text-center max-w-[112px] leading-tight ${isDark ? 'text-white/70 group-hover:text-white/90' : 'text-gray-600 group-hover:text-gray-800'} transition-colors`} data-id="element-2317">
        {item.name}
      </span>
    </button>;
}
// ── Breadcrumbs ──
function Breadcrumbs({
  path,
  onNavigate,
  isDark
}: {
  path: InteriorMenuItem[];
  onNavigate: (index: number) => void;
  isDark: boolean;
}) {
  return <div className="flex items-center gap-1 overflow-x-auto" data-id="element-2318">
      {path.map((item, i) => {
      const isLast = i === path.length - 1;
      return <Fragment key={item.id} data-id="element-2319">
            {i > 0 && <ChevronRight className={`w-3 h-3 flex-shrink-0 ${isDark ? 'text-white/20' : 'text-gray-300'}`} data-id="element-2320" />}
            <button onClick={() => onNavigate(i)} disabled={isLast} className={`text-[11px] font-medium whitespace-nowrap transition-colors px-1 py-0.5 rounded
                ${isLast ? isDark ? 'text-white/80 cursor-default' : 'text-gray-700 cursor-default' : isDark ? 'text-white/40 hover:text-white/70 hover:bg-white/5' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'}`} data-id="element-2321">
              {item.name}
            </button>
          </Fragment>;
    })}
    </div>;
}
// ── Search + Filter Bar ──
function SearchFilterBar({
  searchQuery,
  onSearchChange,
  selectedTiers,
  onToggleTier,
  allOptions,
  filteredCount,
  isDark,
  showTierFilter
}: {
  searchQuery: string;
  onSearchChange: (q: string) => void;
  selectedTiers: Set<string>;
  onToggleTier: (tier: string) => void;
  allOptions: EnrichedOption[];
  filteredCount: number;
  isDark: boolean;
  showTierFilter: boolean;
}) {
  const tierCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const opt of allOptions) {
      counts[opt.tier] = (counts[opt.tier] || 0) + 1;
    }
    return counts;
  }, [allOptions]);
  return <>
      {showTierFilter && <TierMultiSelect selectedTiers={selectedTiers} onToggleTier={onToggleTier} optionTierCounts={tierCounts} totalCount={allOptions.length} isDark={isDark} data-id="element-2322" />}
      <div className="relative flex-1 max-w-[220px] min-w-[100px]" data-id="element-2323">
        <Search className={`absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 ${isDark ? 'text-white/30' : 'text-gray-400'}`} data-id="element-2324" />
        <input type="text" placeholder="Search..." value={searchQuery} onChange={e => onSearchChange(e.target.value)} className={`w-full pl-7 pr-6 py-1 rounded-lg text-[11px] border focus:outline-none transition-colors
            ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-white/30 focus:border-cyan-500/50' : 'bg-white/50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-teal-500/50'}`} data-id="element-2325" />
        {searchQuery && <button onClick={() => onSearchChange('')} className={`absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded-full transition-colors ${isDark ? 'text-white/30 hover:text-white' : 'text-gray-400 hover:text-gray-700'}`} data-id="element-2326">
            <X className="w-3 h-3" data-id="element-2327" />
          </button>}
      </div>
      <span className={`text-[10px] whitespace-nowrap ${isDark ? 'text-white/30' : 'text-gray-400'}`} data-id="element-2328">
        {filteredCount} of {allOptions.length}
      </span>
    </>;
}
// ── Main Component ──
export function SubVariantGallery({
  parentItem,
  onClose,
  onSelectOption,
  category = 'Kitchen'
}: SubVariantGalleryProps) {
  const {
    isDark
  } = useTheme();
  const {
    addSelection,
    selections
  } = useSelections();
  const [viewMode, setViewMode] = useState<ViewMode>('tabs');
  // ── Filter state ──
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTiers, setSelectedTiers] = useState<Set<string>>(new Set());
  const resetFilters = () => {
    setSearchQuery('');
    setSelectedTiers(new Set());
  };
  const handleToggleTier = (tier: string) => {
    if (tier === 'All') {
      setSelectedTiers(new Set());
      return;
    }
    setSelectedTiers(prev => {
      const next = new Set(prev);
      if (next.has(tier)) next.delete(tier);else next.add(tier);
      return next;
    });
  };
  // ── Tabs view state ──
  const tabs = useMemo(() => buildTabs(parentItem), [parentItem]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  // ── Folders view state ──
  const [folderPath, setFolderPath] = useState<InteriorMenuItem[]>([parentItem]);
  // Reset when parent changes
  useEffect(() => {
    setActiveTabIndex(0);
    setFolderPath([parentItem]);
    resetFilters();
  }, [parentItem.id]);
  // Reset filters when primary tab changes
  useEffect(() => {
    resetFilters();
  }, [activeTabIndex]);
  // ── Tabs view logic ──
  const activeTab = tabs[activeTabIndex] || tabs[0];
  let tabRawOptions: InteriorOption[] = [];
  let tabActiveItemName = '';
  let tabActiveItemId = '';
  if (activeTab) {
    tabRawOptions = activeTab.item.options || [];
    tabActiveItemName = activeTab.item.name;
    tabActiveItemId = activeTab.item.id;
  }
  const tabAllEnriched = useMemo(() => enrichOptions(tabRawOptions), [tabActiveItemId]);
  const tabFilteredOptions = useMemo(() => filterAndSortOptions(tabAllEnriched, searchQuery, selectedTiers), [tabAllEnriched, searchQuery, selectedTiers]);
  // ── Folders view logic ──
  const currentFolder = folderPath[folderPath.length - 1];
  const currentFolderRawOptions = currentFolder.options || [];
  const currentFolderEnriched = useMemo(() => enrichOptions(currentFolderRawOptions), [currentFolder.id]);
  const currentFolderFiltered = useMemo(() => filterAndSortOptions(currentFolderEnriched, searchQuery, selectedTiers), [currentFolderEnriched, searchQuery, selectedTiers]);
  const currentFolderHasOptions = currentFolderRawOptions.length > 0;
  const currentFolderChildren = (currentFolder.children || []).filter(c => hasDescendantOptions(c));
  const currentFolderHasChildren = currentFolderChildren.length > 0;
  // Filter folder children by search (when no direct options shown)
  const filteredFolderChildren = useMemo(() => {
    if (!searchQuery.trim()) return currentFolderChildren;
    const q = searchQuery.toLowerCase();
    return currentFolderChildren.filter(c => c.name.toLowerCase().includes(q));
  }, [currentFolderChildren, searchQuery]);
  const handleFolderDrillIn = (child: InteriorMenuItem) => {
    setFolderPath(prev => [...prev, child]);
    resetFilters();
  };
  const handleBreadcrumbNavigate = (index: number) => {
    setFolderPath(prev => prev.slice(0, index + 1));
    resetFilters();
  };
  // ── Selection logic ──
  const getActiveItemName = () => {
    if (viewMode === 'tabs') return tabActiveItemName;
    return currentFolder.name;
  };
  const currentSelection = selections.find(s => s.itemName === getActiveItemName());
  const handleSelect = (option: EnrichedOption, itemName: string) => {
    addSelection({
      category: parentItem.name,
      itemName,
      optionName: option.name,
      optionId: option.id,
      price: option.price,
      thumbnail: option.thumbnail
    });
    onSelectOption(option.id);
  };
  // ── Progress counting ──
  const totalSubVariants = useMemo(() => countConfigurableLeaves(parentItem), [parentItem]);
  const completedSubVariants = useMemo(() => {
    const countCompleted = (item: InteriorMenuItem): number => {
      let count = 0;
      if (item.options && item.options.length > 0) {
        if (selections.some(s => s.itemName === item.name)) count++;
      }
      if (item.children) {
        for (const child of item.children) count += countCompleted(child);
      }
      return count;
    };
    return countCompleted(parentItem);
  }, [parentItem, selections]);
  const hasSelectionInSubtree = (item: InteriorMenuItem): boolean => {
    if (selections.some(s => s.itemName === item.name)) return true;
    if (item.children) return item.children.some(c => hasSelectionInSubtree(c));
    return false;
  };
  if (tabs.length === 0 && !currentFolderHasOptions && !currentFolderHasChildren) {
    return null;
  }
  // Determine which options to use for the search/filter bar
  const activeEnrichedForBar = viewMode === 'tabs' ? tabAllEnriched : currentFolderEnriched;
  const activeFilteredCount = viewMode === 'tabs' ? tabFilteredOptions.length : currentFolderFiltered.length;
  const showTierFilter = viewMode === 'tabs' || currentFolderHasOptions;
  return <div className="absolute left-[260px] right-0 z-30 flex flex-col items-center justify-end pointer-events-none px-6 bottom-[60px]" data-id="element-2329">
      <motion.div initial={{
      opacity: 0,
      y: 30
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: 30
    }} transition={{
      duration: 0.25,
      ease: [0.22, 1, 0.36, 1]
    }} className={`backdrop-blur-xl border shadow-2xl pointer-events-auto overflow-hidden w-full max-w-5xl rounded-2xl flex flex-col
          ${isDark ? 'bg-slate-900/80 border-white/10' : 'bg-white/85 border-white/40'}`} data-id="element-2330">
        {/* ── Header ── */}
        <div className={`flex items-center gap-3 px-4 py-2 border-b flex-shrink-0 ${isDark ? 'border-white/5 bg-white/5' : 'border-gray-200/50 bg-gray-50/50'}`} data-id="element-2331">
          {viewMode === 'folders' ? <Breadcrumbs path={folderPath} onNavigate={handleBreadcrumbNavigate} isDark={isDark} data-id="element-2332" /> : <span className={`text-xs font-bold uppercase tracking-wider whitespace-nowrap ${isDark ? 'text-white/80' : 'text-gray-700'}`} data-id="element-2333">
              {parentItem.name}
            </span>}

          <SearchFilterBar searchQuery={searchQuery} onSearchChange={setSearchQuery} selectedTiers={selectedTiers} onToggleTier={handleToggleTier} allOptions={activeEnrichedForBar} filteredCount={activeFilteredCount} isDark={isDark} showTierFilter={showTierFilter} data-id="element-2334" />

          <div className="flex-1" data-id="element-2335" />

          {/* Progress */}
          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium ${isDark ? 'bg-white/5 text-white/40' : 'bg-gray-100 text-gray-500'}`} data-id="element-2336">
            <span className={`font-bold ${completedSubVariants > 0 ? isDark ? 'text-cyan-400' : 'text-teal-600' : ''}`} data-id="element-2337">
              {completedSubVariants}
            </span>
            <span data-id="element-2338">/</span>
            <span data-id="element-2339">{totalSubVariants}</span>
          </div>

          {/* View mode toggle */}
          <div className={`flex items-center rounded-lg p-0.5 ${isDark ? 'bg-white/5' : 'bg-gray-100'}`} data-id="element-2340">
            <button onClick={() => setViewMode('tabs')} className={`p-1.5 rounded-md transition-all ${viewMode === 'tabs' ? isDark ? 'bg-white/10 text-white' : 'bg-white text-gray-900 shadow-sm' : isDark ? 'text-white/30 hover:text-white/60' : 'text-gray-400 hover:text-gray-600'}`} title="Tabs view" data-id="element-2341">
              <List className="w-3.5 h-3.5" data-id="element-2342" />
            </button>
            <button onClick={() => {
            setViewMode('folders');
            setFolderPath([parentItem]);
            resetFilters();
          }} className={`p-1.5 rounded-md transition-all ${viewMode === 'folders' ? isDark ? 'bg-white/10 text-white' : 'bg-white text-gray-900 shadow-sm' : isDark ? 'text-white/30 hover:text-white/60' : 'text-gray-400 hover:text-gray-600'}`} title="Folder view" data-id="element-2343">
              <LayoutGrid className="w-3.5 h-3.5" data-id="element-2344" />
            </button>
          </div>

          <button onClick={onClose} className={`p-1.5 rounded-full transition-colors ${isDark ? 'hover:bg-white/10 text-white/50 hover:text-white' : 'hover:bg-gray-200 text-gray-400 hover:text-gray-600'}`} data-id="element-2345">
            <ChevronDown className="w-4 h-4" data-id="element-2346" />
          </button>
        </div>

        {/* ── TABS VIEW ── */}
        {viewMode === 'tabs' && <>
            {/* Primary Tabs */}
            <div className={`flex items-center gap-1 px-4 py-1.5 border-b overflow-x-auto flex-shrink-0 ${isDark ? 'border-white/5' : 'border-gray-100'}`} data-id="element-2347">
              {tabs.map((tab, i) => {
            const isActive = activeTabIndex === i;
            const label = tab.item.name;
            const tabHasSelection = selections.some(s => s.itemName === tab.item.name);
            return <button key={tab.item.id} onClick={() => setActiveTabIndex(i)} className={`relative flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all
                      ${isActive ? isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-900' : isDark ? 'text-white/40 hover:text-white/70 hover:bg-white/5' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-50'}`} data-id="element-2348">
                    {label}
                    {tabHasSelection && <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isDark ? 'bg-emerald-400' : 'bg-emerald-500'}`} data-id="element-2349" />}
                    {isActive && <motion.div layoutId="subvariant-tab-indicator" className={`absolute bottom-0 left-2 right-2 h-0.5 rounded-full ${isDark ? 'bg-cyan-400' : 'bg-teal-600'}`} transition={{
                duration: 0.2,
                ease: [0.22, 1, 0.36, 1]
              }} data-id="element-2350" />}
                  </button>;
          })}
            </div>

            {/* Options Grid (tabs) */}
            <div className="flex-1 min-h-0" data-id="element-2351">
              <AnimatePresence mode="wait" data-id="element-2352">
                <motion.div key={tabActiveItemId} initial={{
              opacity: 0,
              x: 10
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -10
            }} transition={{
              duration: 0.15
            }} className="p-4 flex gap-3 overflow-x-auto custom-scrollbar" data-id="element-2353">
                  {tabFilteredOptions.length === 0 ? <div className={`flex items-center justify-center w-full py-8 ${isDark ? 'text-white/20' : 'text-gray-300'}`} data-id="element-2354">
                      <span className="text-sm" data-id="element-2355">
                        {tabAllEnriched.length === 0 ? 'No options available' : 'No matching options'}
                      </span>
                    </div> : tabFilteredOptions.map(option => <OptionCard key={option.id} option={option} isSelected={currentSelection?.optionId === option.id} onSelect={() => handleSelect(option, tabActiveItemName)} isDark={isDark} data-id="element-2356" />)}
                </motion.div>
              </AnimatePresence>
            </div>
          </>}

        {/* ── FOLDERS VIEW ── */}
        {viewMode === 'folders' && <div className="flex-1 min-h-0" data-id="element-2357">
            <AnimatePresence mode="wait" data-id="element-2358">
              <motion.div key={currentFolder.id} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.2,
            ease: [0.22, 1, 0.36, 1]
          }} className="p-4 space-y-4" data-id="element-2359">
                {/* Direct options for current folder */}
                {currentFolderHasOptions && <div data-id="element-2360">
                    {currentFolderHasChildren && <div className={`text-[9px] font-bold uppercase tracking-[0.15em] mb-2 px-1 ${isDark ? 'text-white/25' : 'text-gray-400'}`} data-id="element-2361">
                        Options
                      </div>}
                    <div className="flex gap-3 overflow-x-auto custom-scrollbar pb-1" data-id="element-2362">
                      {currentFolderFiltered.length === 0 ? <div className={`flex items-center justify-center w-full py-6 ${isDark ? 'text-white/20' : 'text-gray-300'}`} data-id="element-2363">
                          <span className="text-sm" data-id="element-2364">No matching options</span>
                        </div> : currentFolderFiltered.map(option => {
                  const folderSelection = selections.find(s => s.itemName === currentFolder.name);
                  return <OptionCard key={option.id} option={option} isSelected={folderSelection?.optionId === option.id} onSelect={() => handleSelect(option, currentFolder.name)} isDark={isDark} data-id="element-2365" />;
                })}
                    </div>
                  </div>}

                {/* Child folders */}
                {currentFolderHasChildren && <div data-id="element-2366">
                    {currentFolderHasOptions && <div className={`text-[9px] font-bold uppercase tracking-[0.15em] mb-2 px-1 ${isDark ? 'text-white/25' : 'text-gray-400'}`} data-id="element-2367">
                        Sub-components
                      </div>}
                    <div className="flex gap-3 overflow-x-auto custom-scrollbar pb-1" data-id="element-2368">
                      {filteredFolderChildren.length === 0 ? <div className={`flex items-center justify-center w-full py-6 ${isDark ? 'text-white/20' : 'text-gray-300'}`} data-id="element-2369">
                          <span className="text-sm" data-id="element-2370">No matching items</span>
                        </div> : filteredFolderChildren.map(child => <FolderCard key={child.id} item={child} onClick={() => handleFolderDrillIn(child)} isDark={isDark} hasSelection={hasSelectionInSubtree(child)} data-id="element-2371" />)}
                    </div>
                  </div>}

                {/* Empty state */}
                {!currentFolderHasOptions && !currentFolderHasChildren && <div className={`flex items-center justify-center py-8 ${isDark ? 'text-white/20' : 'text-gray-300'}`} data-id="element-2372">
                    <span className="text-sm" data-id="element-2373">No options available</span>
                  </div>}
              </motion.div>
            </AnimatePresence>
          </div>}
      </motion.div>
    </div>;
}